/**
 * Google Analytics Provider
 * Wrapper around nuxt-gtag — emits catalog wire names (one gtag event per delivery).
 */

import { BaseAnalyticsProvider } from './base.provider'
import { AnalyticsEvents } from '../types/events'
import { isGoogleAnalyticsSendEnabled } from '../utils/is-google-analytics-send-enabled.utils'
import type { AnalyticsEventProperties } from '../types/properties'

interface GaEventPayload {
  gaEventName: string
  params: Record<string, unknown>
}

export interface GoogleAnalyticsLogger {
  debug?: (message: string, context?: Record<string, unknown>) => void
  warn: (message: string, context?: Record<string, unknown>) => void
  error: (message: string, context?: Record<string, unknown>) => void
}

/** The gtag commands used by analytics registration and event delivery. */
export interface GoogleTagFn {
  (command: 'event', eventName: string, params?: Record<string, unknown>): void
  (command: 'config', measurementId: string, params?: Record<string, unknown>): void
}

/**
 * Keys never sent to GA4 as event params. `contactId`/`contact_id` and `gokollabContactId` /
 * `gokollab_contact_id` identify a CRM record: the actor's own id is already delivered as GA's
 * `user_id`, and a third party's (or GoKollab profile) id must not ride along as an event dimension.
 */
const GA_PII_PARAM_KEYS = new Set([
  'email',
  'phone',
  'full_name',
  'fullName',
  'first_name',
  'last_name',
  'contactId',
  'contact_id',
  'gokollabContactId',
  'gokollab_contact_id',
])

/**
 * One dashboard param, and the catalog params it can be derived from.
 *
 * `target` is the key GoKollab dashboards read; `sources` are tried in order, and `fallback` applies
 * only when none of them carried a value.
 */
interface GokollabParamAlias {
  events: readonly string[]
  target: string
  sources: readonly string[]
  fallback?: unknown
}

const GOKOLLAB_PARAM_ALIASES: readonly GokollabParamAlias[] = [
  { events: [AnalyticsEvents.SEARCH_PERFORMED], target: 'search_text', sources: ['query'] },
  { events: [AnalyticsEvents.GOKOLLAB_CATEGORY_SWITCH], target: 'category', sources: ['category_slug'], fallback: 'all' },
  {
    events: [AnalyticsEvents.GOKOLLAB_LISTING_VIEW, AnalyticsEvents.GOKOLLAB_LISTING_JOINED],
    target: 'slug',
    sources: ['listing_slug'],
  },
  {
    events: [AnalyticsEvents.GOKOLLAB_LISTING_VIEW, AnalyticsEvents.GOKOLLAB_LISTING_JOINED],
    target: 'type',
    sources: ['listing_type', 'entity_type'],
  },
]

/**
 * `logged_in` needs no entry: the legacy key differed from the catalog's `loggedIn` only in case, and
 * `toSnakeCase` already produces it. The old branch for it could never fire.
 */

export class GoogleAnalyticsProvider extends BaseAnalyticsProvider<GaEventPayload> {
  readonly name = 'GoogleAnalytics'

  private gtag: GoogleTagFn | null = null
  private debug = false
  private appEnv = 'development'
  private customLoginFired = false
  private logger: GoogleAnalyticsLogger | null = null

  constructor(debug = false, logger?: GoogleAnalyticsLogger) {
    super()
    this.debug = debug
    this.logger = logger ?? null
  }

  setAppEnv(appEnv: string): void {
    this.appEnv = appEnv
  }

  setGtag(gtag: GoogleTagFn): void {
    this.gtag = gtag
    if (this.debug) this.logger?.debug?.('[GoogleAnalytics] gtag instance set')
  }

  /** @internal test reset */
  resetSessionFlagsForTests(): void {
    this.customLoginFired = false
  }

  private get canSendToGoogleAnalytics(): boolean {
    return isGoogleAnalyticsSendEnabled(this.appEnv)
  }

  transform(event: string, properties: AnalyticsEventProperties): GaEventPayload | null {
    const gaEventName = event.trim()
    if (!gaEventName) return null

    const params = this.applyGokollabParamAliases(gaEventName, this.toSnakeCase(this.stripGaPii(this.flattenGaParams(properties))))

    return { gaEventName, params }
  }

  /** Merges v-track `extra` bag into top-level params for flat GA4 key-value pairs. */
  private flattenGaParams(properties: AnalyticsEventProperties): Record<string, unknown> {
    // `pendoProperties` is a Pendo-only nested bag; keep it out of GA's flat params.
    const { userId: _userId, contactId, extra, pendoProperties: _pendoProperties, ...rest } = properties
    const extraBag = extra && typeof extra === 'object' && !Array.isArray(extra) ? (extra as Record<string, unknown>) : {}

    return {
      ...rest,
      ...extraBag,
      ...(contactId ? { user_id: contactId } : {}),
    }
  }

  /**
   * Maps catalog property names to dashboard param keys for GoKollab.
   *
   * A table rather than a chain of per-event branches: every rule is the same shape, so the branches
   * only expressed data, and expressing them as code pushed this past the complexity gate. `sources`
   * are snake_case because `toSnakeCase` has already run on every key by this point.
   */
  private applyGokollabParamAliases(gaEventName: string, params: Record<string, unknown>): Record<string, unknown> {
    const result = { ...params }

    for (const alias of GOKOLLAB_PARAM_ALIASES) {
      if (!alias.events.includes(gaEventName)) continue
      // An explicitly supplied dashboard key always wins over a derived one.
      if (result[alias.target] !== undefined) continue

      const carried = alias.sources.map(source => result[source]).find(value => value !== undefined && value !== null)
      const resolved = carried ?? alias.fallback
      if (resolved !== undefined) result[alias.target] = resolved
    }

    return result
  }

  send({ gaEventName, params }: GaEventPayload): void {
    if (!this.gtag) {
      if (this.debug) this.logger?.warn('[GoogleAnalytics] gtag not available')
      return
    }

    if (!this.canSendToGoogleAnalytics) {
      if (this.debug) this.logger?.debug?.('[GoogleAnalytics] Event skipped outside enabled environment', { eventName: gaEventName })
      return
    }

    if (gaEventName === AnalyticsEvents.LOGIN_SUCCESS) {
      if (this.customLoginFired) {
        return
      }
      this.customLoginFired = true
    }

    this.emitEvent(gaEventName, params)
  }

  private stripGaPii(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      if (GA_PII_PARAM_KEYS.has(key)) continue
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      if (GA_PII_PARAM_KEYS.has(snakeKey)) continue
      result[key] = value
    }
    return result
  }

  private emitEvent(gaEventName: string, params: Record<string, unknown>): void {
    if (!this.gtag) return
    try {
      this.gtag('event', gaEventName, params)
      if (this.debug) this.logger?.debug?.('[GoogleAnalytics] Event tracked', { eventName: gaEventName })
    } catch (error) {
      this.logger?.error('[GoogleAnalytics] Event delivery failed', { eventName: gaEventName, error })
    }
  }

  private toSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const key of Object.keys(obj)) {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      result[snakeKey] = obj[key]
    }
    return result
  }
}
